import type { FunctionComponent } from 'react';

import { HistorySimulationStatus, IHistoryGame } from '../../../../api/fetchers/history/types';
import { useAudioTrigger } from '../../../../controllers/audioController/hooks';
import { useSettingsSlice } from '../../../../store/slices/settings/selectors';
import { useModifiers } from '../../../../styles/hooks/useModifiers';
import { CurrencyConnected } from '../../../currency/CurrencyConnected';
import { L12nText } from '../../../text/L12nText';

import styles from './HistoryGame.module.css';

export interface HistoryDayProps {
  game: IHistoryGame;
  showDate: boolean;
  onSelect?: (id: IHistoryGame) => void;
}

const dateConfig: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

export const HistoryGame: FunctionComponent<HistoryDayProps> = ({ game, onSelect, showDate }: HistoryDayProps) => {
  const currentLanguage = useSettingsSlice.currentLanguage();
  const audioTrigger = useAudioTrigger();
  const mods = useModifiers(styles, 'info', {
    cancelled: game.simulationStatus === HistorySimulationStatus.Cancelled,
    simulated: game.simulationStatus === HistorySimulationStatus.Simulated,
  });
  const isRefunded = game.simulationStatus === HistorySimulationStatus.Cancelled && Number(game.netAmount) == 0;

  // LCE-75161: There is no dedicated CN date format, only zh or zh-CN
  const currentLanguageValue = currentLanguage.toLocaleLowerCase() === 'cn' ? 'zh' : currentLanguage;

  return (
    <>
      {showDate ? (
        <div className={styles.title}>{new Date(game.day).toLocaleDateString(currentLanguageValue, dateConfig)}</div>
      ) : null}
      <button
        type="button"
        className={`${styles.info} ${mods}`}
        onClick={(): void => {
          onSelect?.(game);
          audioTrigger.play({ name: 'menuClick' });
        }}
      >
        <div className={styles.text}>
          <L12nText tk={game.label} />
        </div>
        <div className={`${styles.text} ${styles.net}`}>
          <CurrencyConnected value={game.stake} />
        </div>
        <div className={`${styles.text} ${styles.date}`}>{game.dataTimeStart.substring(11)}</div>
        <div className={`${styles.text} ${game.netAmount > 0 ? styles.win : styles.lose}`}>
          {isRefunded ? (
            <span className={styles.refunded}>
              <L12nText tk="REFUNDED" />
            </span>
          ) : (
            <CurrencyConnected value={game.netAmount} />
          )}
        </div>
      </button>
    </>
  );
};
